import type { AxiosResponse } from 'axios'
import axios, { isAxiosError } from 'axios'
import { signOut } from 'next-auth/react'

import { toast } from 'react-toastify'

import { convertPlantextToEncrypted, convertEncryptedToPlantext } from '@/utils/encrypt-decrypt'

import getAuthToken from '@/utils/Auth'
import { refreshAccessToken } from '@/utils/Actions/refreshAccessToken'
import { store } from '@/redux-store'

const AuthClient = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_URL,
  headers: {
    'Accept-Language': 'en'
  }
})

let isLoggingOut = false

AuthClient.interceptors.request.use(
  async config => {
    const authToken = await getAuthToken()

    // Attach the Authorization header if authToken exists
    if (authToken) {
      config.headers.Authorization = `Bearer ${authToken}`
    }

    // Get language from Redux store
    try {
      const state = store.getState()
      const language = state.languageReducer.current || 'en'

      config.headers['Accept-Language'] = language
    } catch (error) {
      // Fall back to default language
      config.headers['Accept-Language'] = 'en'
    }

    if (config.data) {
      // Encrypt the request payload before sending it to the API
      // config.headers['Content-Type'] = 'text/plain'

      config.data = convertPlantextToEncrypted(config.data)
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

AuthClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data) {
      //Decrypt the response received from the API
      response.data = convertEncryptedToPlantext(response?.data?.data)
    }

    return response
  },
  async error => {
    const originalRequest = error.config

    if (isAxiosError(error)) {
      if (error?.response?.data) {
        try {
          // Decrypt error response before rejecting
          const decryptedError = convertEncryptedToPlantext(error?.response?.data?.data)

          error.response.data = decryptedError
        } catch (decryptError) {
          Promise.reject(decryptError)
        }
      }

      // 🛑 Handle 459: User Updated (Force Logout)
      if (error.response?.status === 459) {
        if (!isLoggingOut) {
          isLoggingOut = true
          toast.error(error?.response?.data?.message ?? 'Something went wrong')

          // Logout API (fail silently if it errors)
          AuthClient.get('/auth/signout').catch(err => console.error('Logout API failed:', err))

          // Logout user from NextAuth
          await signOut({ callbackUrl: '/login', redirect: true })
          isLoggingOut = false
        }

        return Promise.reject() // Stop further processing to prevent duplicate errors
      }

      // Handle 401 Unauthorized errors (token expired)
      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true // Mark the request as retried

        try {
          const refreshTokenData: any = await getAuthToken(true)

          const { token, error } = refreshTokenData ?? {}

          if (error === 'RefreshTokenExpired') {
            throw new Error('Refresh token expired, Please login again.')
          }

          if (!token) {
            throw new Error('No refresh token available, logging out...')
          }

          // Refresh the token
          const newToken = await refreshAccessToken(token)

          // Update the Authorization header with the new token
          if ('access_token' in newToken) {
            originalRequest.headers.Authorization = `Bearer ${newToken.access_token}`
          } else {
            throw new Error('Failed to refresh token')
          }

          // Retry the original request with the new token
          return AuthClient(originalRequest)
        } catch (refreshError) {
          toast.error((refreshError as Error)?.message ?? 'An unexpected error occurred')

          // Log out the user and redirect to the login page
          await AuthClient.get('/auth/signout').catch(err => console.error('Logout API failed:', err))
          signOut({ callbackUrl: '/login', redirect: true })

          return Promise.reject(refreshError)
        }
      }
    }

    return Promise.reject(error) // Ensure the promise is properly rejected
  }
)

export default AuthClient
